import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Building2,
  ClipboardList,
  CreditCard,
  Download,
  FileText,
  Package,
  ReceiptText,
  RefreshCw,
  ShoppingCart,
  Store,
  UserCheck,
  UserCog,
  Users,
  WalletCards
} from 'lucide-react';
import { API_URL } from '../config/api';
import { formatDate } from '../utils/dateFormat';
import './Reports.css';

const numberValue = (value) => Number(value || 0);
const formatMoney = (value) => `Rs. ${numberValue(value).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export default function Reports({ initialReport = '', hiddenReportTypes = [], onSelectReport }) {
  const [loading, setLoading] = useState('');
  const [previewLoading, setPreviewLoading] = useState('');
  const [previewReport, setPreviewReport] = useState(null);
  const [reportUsers, setReportUsers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
  const [selectedGroupKey, setSelectedGroupKey] = useState('all');
  const [userLoading, setUserLoading] = useState(false);
  const [warehouseLoading, setWarehouseLoading] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [datePreset, setDatePreset] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch (err) {
      return {};
    }
  }, []);
  const canViewInventoryReports = ['superadmin', 'admin', 'whitelabel_admin'].includes(user?.role);
  const canViewActivityReports = ['superadmin', 'admin', 'whitelabel_admin'].includes(user?.role);
  const canViewPurchaseReports = ['superadmin', 'admin', 'whitelabel_admin'].includes(user?.role);
  const canUseUserFilter = ['superadmin', 'admin', 'whitelabel_admin', 'employee'].includes(user?.role);
  const canUseOfficeFilter = ['superadmin', 'admin', 'whitelabel_admin', 'employee'].includes(user?.role);
  const hiddenReportKey = useMemo(() => (hiddenReportTypes || []).join('|'), [hiddenReportTypes]);

  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  const reports = useMemo(() => {
    const hiddenSet = new Set(hiddenReportKey ? hiddenReportKey.split('|') : []);
    return [
      { type: 'sales-daily', label: 'Daily Sales Report', code: 'DS', color: '#3b82f6', desc: "Today's complete sales list", Icon: FileText },
      { type: 'sales-monthly', label: 'Monthly Sales Report', code: 'MS', color: '#10b981', desc: 'All sales for this month', Icon: FileText },
      { type: 'device-sales', label: 'Device Sales Report', code: 'DV', color: '#0b66ae', desc: 'Device sale entries with dealer tagging', Icon: ShoppingCart },
      { type: 'service-sales', label: 'Service Sales Report', code: 'SV', color: '#f59e0b', desc: 'Service sale entries with tagged dealer', Icon: ReceiptText },
      { type: 'sales-all', label: 'All Combined Sales Report', code: 'AS', color: '#3b82f6', desc: 'Device and Service sales combined', Icon: ShoppingCart },
      { type: 'employee-wise-sales', label: 'Employee Wise Sales Report', code: 'ES', color: '#0891b2', desc: 'Detailed employee sales & payments', Icon: Users },
      { type: 'office-wise-sales', label: 'Office Wise Sales Report', code: 'OW', color: '#8b5cf6', desc: 'Office wise device, service, and payment sales', Icon: Building2 },
      { type: 'customers', label: 'Customers Report', code: 'CR', color: '#8b5cf6', desc: 'Complete list of all customers', Icon: UserCog },
      ...(canViewInventoryReports
        ? [{ type: 'inventory', label: 'Inventory Report', code: 'IR', color: '#f59e0b', desc: 'Stock and devices list', Icon: Package }]
        : []),
      { type: 'payments', label: 'All Payment Report', code: 'AP', color: '#10b981', desc: 'Payment history records', Icon: CreditCard },
      { type: 'payments-debit-credit', label: 'Debit Credit Payment Report', code: 'PD', color: '#0f766e', desc: 'Combined payment credits and invoice debits', Icon: CreditCard },
      { type: 'payment-report-customer', label: 'Customer Payment Report', code: 'CP', color: '#047857', desc: 'Customer payment records', Icon: UserCog },
      { type: 'payment-report-dealer', label: 'Dealer Payment Report', code: 'DP', color: '#2563eb', desc: 'Dealer payment records', Icon: Store },
      ...(canViewPurchaseReports
        ? [{ type: 'payment-report-purchase', label: 'Purchase Payment Report', code: 'PU', color: '#f97316', desc: 'Purchase and vendor payment records', Icon: ShoppingCart }]
        : []),
      { type: 'credit-debit-ledger', label: 'All Credit/Debit Ledger', code: 'AL', color: '#0f766e', desc: 'All account ledger balances', Icon: WalletCards },
      { type: 'ledger-report-admin', label: 'Admin Credit/Debit Ledger', code: 'AD', color: '#7c3aed', desc: 'Admin ledger balances', Icon: Building2 },
      { type: 'ledger-report-employee', label: 'Employee Credit/Debit Ledger', code: 'EL', color: '#0891b2', desc: 'Employee ledger balances', Icon: UserCheck },
      { type: 'ledger-report-dealer', label: 'Dealer Credit/Debit Ledger Details', code: 'DL', color: '#6366f1', desc: 'Detailed dealer credit and debit ledger', Icon: Store },
      { type: 'renewals', label: 'Renewals Report', code: 'RR', color: '#ef4444', desc: 'Customers with upcoming renewals', Icon: RefreshCw },
      { type: 'pending-payments', label: 'Pending Payments', code: 'PP', color: '#dc2626', desc: 'Customers with pending payments', Icon: CreditCard },
      ...(['superadmin', 'admin', 'whitelabel_admin', 'employee', 'dealer'].includes(user?.role)
        ? [{
          type: 'dealer-wise',
          label: user?.role === 'dealer' ? 'My Dealer Ledger Report' : 'Dealer Wise Report',
          code: 'DW',
          color: '#6366f1',
          desc: user?.role === 'dealer' ? 'Your tagged customer, sales and payment ledger' : 'Sales and payments per dealer',
          Icon: Store
        }]
        : []),
      ...(canViewActivityReports
        ? [{ type: 'activity-history', label: 'Activity History Report', code: 'AH', color: '#0f766e', desc: 'Workspace action logs', downloadPath: 'activity-history/download', Icon: ClipboardList }]
        : [])
    ].filter((report) => !hiddenSet.has(report.type));
  }, [canViewActivityReports, canViewInventoryReports, canViewPurchaseReports, hiddenReportKey, user?.role]);

  const reportGroups = useMemo(() => {
    const reportByType = new Map(reports.map((report) => [report.type, report]));
    return [
      { key: 'reports-sales', title: 'Sales', subtitle: 'Sales reports', color: '#0b66ae', Icon: ShoppingCart, items: ['sales-daily', 'sales-monthly', 'device-sales', 'service-sales', 'sales-all', 'employee-wise-sales', 'office-wise-sales'] },
      { key: 'reports-customers', title: 'Customers', subtitle: 'Customer report', color: '#8b5cf6', Icon: UserCog, items: ['customers'] },
      { key: 'reports-inventory', title: 'Inventory', subtitle: 'Inventory reports', color: '#f59e0b', Icon: Package, items: ['inventory'] },
      { key: 'reports-finance', title: 'Finance', subtitle: 'Payment reports', color: '#10b981', Icon: WalletCards, items: ['payments', 'payments-debit-credit', 'payment-report-customer', 'payment-report-dealer', 'payment-report-purchase', 'credit-debit-ledger', 'ledger-report-admin', 'ledger-report-employee', 'ledger-report-dealer', 'pending-payments', 'dealer-wise'] },
      { key: 'reports-renewal', title: 'Renewal', subtitle: 'Renewal reports', color: '#ef4444', Icon: RefreshCw, items: ['renewals'] },
      { key: 'reports-activity', title: 'Activity', subtitle: 'Activity reports', color: '#0f766e', Icon: ClipboardList, items: ['activity-history'] }
    ]
      .map((group) => ({
        ...group,
        reports: group.items.map((type) => reportByType.get(type)).filter(Boolean)
      }))
      .filter((group) => group.reports.length > 0);
  }, [reports]);

  const reportGroupByType = useMemo(() => {
    const map = new Map();
    reportGroups.forEach((group) => {
      group.reports.forEach((report) => map.set(report.type, group));
    });
    return map;
  }, [reportGroups]);


  useEffect(() => {
    if (!canUseUserFilter) return;
    let cancelled = false;
    const loadUsers = async () => {
      setUserLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/users`, { headers: getHeaders() });
        if (!cancelled) {
          setReportUsers((res.data || []).filter((item) => item.is_active !== 0));
        }
      } catch (err) {
        if (!cancelled) setReportUsers([]);
      } finally {
        if (!cancelled) setUserLoading(false);
      }
    };
    loadUsers();
    return () => {
      cancelled = true;
    };
  }, [canUseUserFilter, getHeaders]);

  useEffect(() => {
    if (!canUseOfficeFilter) return;
    let cancelled = false;
    const loadWarehouses = async () => {
      setWarehouseLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/inventory/warehouses`, { headers: getHeaders() });
        if (!cancelled) setWarehouses((res.data || []).filter((item) => Number(item.is_active ?? 1) === 1));
      } catch (err) {
        if (!cancelled) setWarehouses([]);
      } finally {
        if (!cancelled) setWarehouseLoading(false);
      }
    };
    loadWarehouses();
    return () => {
      cancelled = true;
    };
  }, [canUseOfficeFilter, getHeaders]);

  const selectedReportUser = useMemo(() => (
    reportUsers.find((item) => String(item.id) === String(selectedUserId)) || null
  ), [reportUsers, selectedUserId]);

  const selectedWarehouse = useMemo(() => (
    warehouses.find((item) => String(item.id) === String(selectedWarehouseId)) || null
  ), [selectedWarehouseId, warehouses]);

  // Related reports list removed for simplified UI

  const reportParams = useCallback((userId = selectedUserId, warehouseId = selectedWarehouseId) => {
    const params = {};
    const selected = reportUsers.find((item) => String(item.id) === String(userId));
    if (user?.role === 'superadmin' && ['admin', 'whitelabel_admin'].includes(selected?.role)) {
      params.admin_id = userId;
    } else if (userId) {
      params.user_id = userId;
    }
    if (warehouseId) params.warehouse_id = warehouseId;
    return params;
  }, [reportUsers, selectedUserId, selectedWarehouseId, user?.role]);

  const formatRole = (role = '') => String(role || '').replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

  const downloadReport = async (reportOrType, labelArg = '', userId = selectedUserId, warehouseId = selectedWarehouseId) => {
    const report = typeof reportOrType === 'string' ? { type: reportOrType, label: labelArg } : reportOrType;
    const type = report.type;
    const label = report.label || labelArg || type;
    const path = report.downloadPath || type;
    setLoading(type);
    try {
      const params = reportParams(userId, warehouseId);
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      const res = await axios.get(`${API_URL}/api/reports/${path}`, {
        headers: getHeaders(),
        responseType: 'blob',
        params
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      const userFilterName = selectedReportUser && String(userId) === String(selectedReportUser.id)
        ? `_${selectedReportUser.name}`.replace(/[^\w-]+/g, '_')
        : '';
      const officeFilterName = selectedWarehouse && String(warehouseId) === String(selectedWarehouse.id)
        ? `_${selectedWarehouse.name}`.replace(/[^\w-]+/g, '_')
        : '';
      a.href = url;
      a.download = `${label}${userFilterName}${officeFilterName}_${formatDate(new Date()).replace(/\//g, '-')}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Report download failed');
    } finally {
      setLoading('');
    }
  };

  const viewReport = useCallback(async (report, userId = selectedUserId, warehouseId = selectedWarehouseId, fromD = fromDate, toD = toDate) => {
    setPreviewReport({
      ...report,
      title: report.label,
      rows: [],
      loadedAt: '',
      isLoading: true
    });
    setPreviewLoading(report.type);
    try {
      const params = reportParams(userId, warehouseId);
      if (fromD) params.from_date = fromD;
      if (toD) params.to_date = toD;

      const res = await axios.get(`${API_URL}/api/reports/${report.type}/view`, {
        headers: getHeaders(),
        params
      });
      setPreviewReport({
        ...report,
        title: res.data?.title || report.label,
        rows: res.data?.rows || [],
        loadedAt: new Date().toLocaleString('en-IN'),
        isLoading: false,
        error: ''
      });
    } catch (err) {
      setPreviewReport({
        ...report,
        title: report.label,
        rows: [],
        loadedAt: '',
        isLoading: false,
        error: err.response?.data?.message || 'Report preview failed'
      });
    } finally {
      setPreviewLoading('');
    }
  }, [getHeaders, reportParams, selectedUserId, selectedWarehouseId, fromDate, toDate]);

  const handleUserFilterChange = (value) => {
    setSelectedUserId(value);
    if (previewReport) viewReport(previewReport, value, selectedWarehouseId, fromDate, toDate);
  };

  const handleWarehouseFilterChange = (value) => {
    setSelectedWarehouseId(value);
    if (previewReport) viewReport(previewReport, selectedUserId, value, fromDate, toDate);
  };

  const applyDatePreset = (preset) => {
    setDatePreset(preset);
    if (!preset) return;

    const today = new Date();
    let start = new Date();
    let end = new Date();

    switch (preset) {
      case 'today':
        start = new Date();
        end = new Date();
        break;
      case 'yesterday':
        start.setDate(today.getDate() - 1);
        end.setDate(today.getDate() - 1);
        break;
      case 'this-week': {
        const day = today.getDay();
        const diff = today.getDate() - day + (day === 0 ? -6 : 1);
        start.setDate(diff);
        break;
      }
      case 'this-month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'last-month':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'last-30-days':
        start.setDate(today.getDate() - 30);
        break;
      default:
        break;
    }

    const formatYMD = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const fromStr = formatYMD(start);
    const toStr = formatYMD(end);
    setFromDate(fromStr);
    setToDate(toStr);

    if (previewReport) {
      viewReport(previewReport, selectedUserId, selectedWarehouseId, fromStr, toStr);
    }
  };

  const handleFromDateChange = (val) => {
    setFromDate(val);
    setDatePreset('');
    if (previewReport) {
      viewReport(previewReport, selectedUserId, selectedWarehouseId, val, toDate);
    }
  };

  const handleToDateChange = (val) => {
    setToDate(val);
    setDatePreset('');
    if (previewReport) {
      viewReport(previewReport, selectedUserId, selectedWarehouseId, fromDate, val);
    }
  };

  const clearReportFilters = () => {
    setSelectedUserId('');
    setSelectedWarehouseId('');
    setFromDate('');
    setToDate('');
    setDatePreset('');
    setSearchTerm('');
    if (previewReport) viewReport(previewReport, '', '', '', '');
  };

  useEffect(() => {
    if (!initialReport) {
      setPreviewReport(null);
      setSelectedGroupKey('all');
      return;
    }
    const group = reportGroups.find((item) => item.key === initialReport);
    if (group) {
      setPreviewReport(null);
      setSelectedGroupKey(group.key);
      return;
    }
    const report = reports.find((item) => item.label === initialReport || item.type === initialReport);
    if (report) {
      const reportGroup = reportGroupByType.get(report.type);
      setSelectedGroupKey(reportGroup?.key || 'all');
      viewReport(report);
    }
  }, [initialReport, reportGroupByType, reportGroups, reports, viewReport]);

  useEffect(() => {
    if (previewReport?.type && !reports.some((report) => report.type === previewReport.type)) {
      setPreviewReport(null);
    }
  }, [previewReport?.type, reports]);

  const activeGroup = selectedGroupKey === 'all'
    ? null
    : reportGroups.find((group) => group.key === selectedGroupKey) || null;
  const isPreviewBusy = Boolean(previewReport?.isLoading || (previewReport?.type && previewLoading === previewReport.type));
  const visiblePreviewRows = useMemo(() => {
    const rows = previewReport?.rows || [];
    if (!searchTerm.trim()) return rows;
    const query = searchTerm.toLowerCase().trim();
    return rows.filter((row) => {
      return Object.values(row || {}).some((val) => 
        String(val ?? '').toLowerCase().includes(query)
      );
    });
  }, [previewReport, searchTerm]);
  const headerTitle = previewReport?.title || activeGroup?.title || 'Reports';
  const activeFilterParts = [
    selectedReportUser ? `${selectedReportUser.name} (${formatRole(selectedReportUser.role)})` : '',
    selectedWarehouse ? selectedWarehouse.name : ''
  ].filter(Boolean);
  const activeFilterText = activeFilterParts.join(' | ');
  const headerSubtitle = previewReport
    ? `${visiblePreviewRows.length} rows${activeFilterText ? ` | ${activeFilterText}` : ''}${previewReport.loadedAt ? ` | ${previewReport.loadedAt}` : ''}`
    : activeGroup
      ? `${activeGroup.title} category selected`
      : 'Select a report category or use the sidebar';

  const previewColumns = useMemo(() => {
    if (!visiblePreviewRows.length) return [];
    const columns = new Set();
    visiblePreviewRows.slice(0, 20).forEach((row) => Object.keys(row || {}).forEach((key) => columns.add(key)));
    return Array.from(columns);
  }, [visiblePreviewRows]);

  const formatColumnLabel = (key) => String(key || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

  const isNumericColumn = (colName) => {
    return /price|amount|debit|credit|balance|paid|charge|fee/i.test(colName);
  };

  const dynamicKPIs = useMemo(() => {
    if (!previewReport || !visiblePreviewRows.length || !previewColumns.length) return [];
    const kpis = [];
    previewColumns.forEach((col) => {
      if (isNumericColumn(col)) {
        let total = 0;
        visiblePreviewRows.forEach((row) => {
          if (previewReport.type === 'dealer-wise' && row.row_type !== 'Dealer Total') return;
          total += numberValue(row[col]);
        });

        let color = '#2563eb';
        if (/debit|charge|fee/i.test(col)) color = '#dc2626';
        if (/credit|paid/i.test(col)) color = '#059669';
        if (/balance/i.test(col)) color = total > 0 ? '#dc2626' : '#059669';

        kpis.push({
          key: col,
          label: formatColumnLabel(col),
          value: formatMoney(total),
          color
        });
      }
    });
    return kpis;
  }, [previewReport, visiblePreviewRows, previewColumns]);

  const formatCell = (columnName, value) => {
    if (value === null || value === undefined || value === '') return '-';
    const colLower = columnName.toLowerCase();
    const valStr = String(value);
    const valLower = valStr.toLowerCase();

    // Format statuses with badges
    if (colLower.includes('status') || colLower === 'status' || colLower.includes('state') || colLower === 'status_label' || colLower === 'status_code') {
      let badgeClass = 'reports-badge';
      if (['paid', 'active', 'success', 'completed', 'available', 'instock', 'approved'].includes(valLower)) {
        badgeClass += ' success';
      } else if (['pending', 'partial', 'warning', 'in-progress'].includes(valLower)) {
        badgeClass += ' warning';
      } else if (['expired', 'inactive', 'failed', 'danger', 'rejected', 'sold', 'lost', 'defective'].includes(valLower)) {
        badgeClass += ' danger';
      }
      return <span className={badgeClass}>{valStr.toUpperCase()}</span>;
    }

    // Format money / monetary fields
    if (isNumericColumn(columnName) && !isNaN(value) && value !== '') {
      return formatMoney(value);
    }

    // Format dates
    if (colLower.includes('date') && valStr.match(/^\d{4}-\d{2}-\d{2}/)) {
      try {
        return formatDate(new Date(valStr));
      } catch (e) {
        return valStr;
      }
    }

    return valStr;
  };

  const getColumnAlignment = (colName) => {
    if (isNumericColumn(colName)) return 'text-right';
    const colLower = colName.toLowerCase();
    if (colLower.includes('date') || colLower === 'id' || colLower.includes('mobile') || colLower.includes('phone') || colLower === 'imei' || colLower.includes('sim') || colLower.includes('vehicle') || colLower.includes('status') || colLower.includes('invoice') || colLower.includes('ref') || colLower === 'code') {
      return 'text-center';
    }
    return 'text-left';
  };

  // Scroll management simplified

  return (
    <div className="fixed-panel-page reports-workspace">
      <div className="fixed-panel-header reports-header">
        <div className="reports-title-row">
          <div className="reports-title-copy">
            <span>{previewReport ? 'Selected Report' : activeGroup ? 'Report Group' : 'Report Center'}</span>
            <h2>{headerTitle}</h2>
            <p>{headerSubtitle}</p>
          </div>

          <div className="reports-header-actions">
            {!previewReport && selectedGroupKey !== 'all' && (
              <button
                type="button"
                className="reports-btn reports-btn-light"
                onClick={() => {
                  if (onSelectReport) {
                    onSelectReport('');
                  } else {
                    setSelectedGroupKey('all');
                  }
                }}
              >
                Back to Categories
              </button>
            )}
            {previewReport && (
              <button
                type="button"
                className="reports-btn reports-btn-light"
                onClick={() => {
                  setPreviewReport(null);
                  if (onSelectReport) {
                    onSelectReport(selectedGroupKey !== 'all' ? selectedGroupKey : '');
                  }
                }}
                disabled={isPreviewBusy}
              >
                Close Report
              </button>
            )}
            {previewReport && (
              <button type="button" className="reports-btn reports-btn-light" onClick={() => viewReport(previewReport)} disabled={isPreviewBusy}>
                <FileText size={16} /> {isPreviewBusy ? 'Loading...' : 'Show Report'}
              </button>
            )}
            {previewReport && (
              <button type="button" className="reports-btn reports-btn-primary" onClick={() => downloadReport(previewReport)} disabled={loading === previewReport.type || isPreviewBusy} style={{ '--report-color': previewReport.color }}>
                <Download size={16} /> {loading === previewReport.type ? 'Downloading...' : 'Download Excel'}
              </button>
            )}
          </div>
        </div>

        <div className="reports-filter-shell">
          <div className="reports-filter-grid">
            {canUseUserFilter && (
              <label>
                <span>User</span>
                <select
                  value={selectedUserId}
                  onChange={(event) => handleUserFilterChange(event.target.value)}
                  disabled={userLoading}
                  aria-label="Filter report by user"
                >
                  <option value="">{userLoading ? 'Loading users...' : 'All Users'}</option>
                  {reportUsers.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name} - {formatRole(item.role)}{item.mobile ? ` - ${item.mobile}` : ''}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {canUseOfficeFilter && (
              <label>
                <span>Office Location</span>
                <select
                  value={selectedWarehouseId}
                  onChange={(event) => handleWarehouseFilterChange(event.target.value)}
                  disabled={warehouseLoading}
                  aria-label="Filter report by office location"
                >
                  <option value="">{warehouseLoading ? 'Loading locations...' : 'All Locations'}</option>
                  {warehouses.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}{item.admin_name ? ` - ${item.admin_name}` : ''}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <label>
              <span>From Date</span>
              <input
                type="date"
                value={fromDate}
                onChange={(event) => handleFromDateChange(event.target.value)}
                aria-label="Filter report from date"
              />
            </label>

            <label>
              <span>To Date</span>
              <input
                type="date"
                value={toDate}
                onChange={(event) => handleToDateChange(event.target.value)}
                aria-label="Filter report to date"
              />
            </label>

            <label>
              <span>Quick Presets</span>
              <select
                value={datePreset}
                onChange={(event) => applyDatePreset(event.target.value)}
                aria-label="Filter report quick preset date range"
              >
                <option value="">Custom Range</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="this-week">This Week</option>
                <option value="this-month">This Month</option>
                <option value="last-month">Last Month</option>
                <option value="last-30-days">Last 30 Days</option>
              </select>
            </label>

            {(selectedUserId || selectedWarehouseId || fromDate || toDate || searchTerm) && (
              <div style={{ display: 'flex', alignItems: 'flex-end', minHeight: '40px' }}>
                <button type="button" className="reports-btn reports-btn-light" onClick={clearReportFilters} style={{ width: '100%', minHeight: '40px' }}>
                  Reset Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="fixed-panel-body reports-body">
        {!previewReport && (
          selectedGroupKey === 'all' ? (
            <div className="reports-category-grid">
              {reportGroups.map((group) => (
                <div
                  className="reports-category-card"
                  key={group.key}
                  style={{ '--report-color': group.color }}
                  onClick={() => {
                    if (onSelectReport) {
                      onSelectReport(group.key);
                    } else {
                      setSelectedGroupKey(group.key);
                    }
                  }}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      if (onSelectReport) {
                        onSelectReport(group.key);
                      } else {
                        setSelectedGroupKey(group.key);
                      }
                    }
                  }}
                >
                  <div className="reports-category-icon">
                    <group.Icon size={24} />
                  </div>
                  <div className="reports-category-copy">
                    <h3>{group.title}</h3>
                    <p>{group.subtitle}</p>
                    <span className="reports-category-count">
                      {group.reports.length} {group.reports.length === 1 ? 'Report' : 'Reports'} Available
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="reports-group-list">
              {reportGroups
                .filter((group) => group.key === selectedGroupKey)
                .map((group) => (
                  <div className="reports-group-section" key={group.key} style={{ '--report-color': group.color }}>
                    <div className="reports-group-heading">
                      <div className="reports-group-title">
                        <span><group.Icon size={18} /></span>
                        <div>
                          <h3>{group.title} Reports</h3>
                          <p>{group.subtitle}</p>
                        </div>
                      </div>
                      <strong>{group.reports.length} Reports</strong>
                    </div>
                    <div className="reports-card-grid">
                      {group.reports.map((report) => (
                        <div
                          className="reports-card"
                          onClick={() => {
                            if (onSelectReport) {
                              onSelectReport(report.type);
                            } else {
                              viewReport(report);
                            }
                          }}
                          key={report.type}
                          style={{ '--report-color': report.color }}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              if (onSelectReport) {
                                onSelectReport(report.type);
                              } else {
                                viewReport(report);
                              }
                            }
                          }}
                        >
                          <div className="reports-card-icon">
                            <report.Icon size={18} />
                          </div>
                          <div className="reports-card-copy">
                            <strong>{report.label}</strong>
                            <small>{report.desc}</small>
                          </div>
                          <button
                            type="button"
                            className="reports-card-action"
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadReport(report);
                            }}
                            disabled={loading === report.type}
                          >
                            <Download size={14} /> Download
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          )
        )}

      {previewReport && (
        <section className="reports-preview-panel" style={{ '--report-color': previewReport.color }}>
          {dynamicKPIs.length > 0 && (
            <div className="reports-summary-grid">
              {dynamicKPIs.map((card) => (
                <div key={card.key} className="reports-summary-card">
                  <span>{card.label}</span>
                  <strong style={{ color: card.color }}>{card.value}</strong>
                </div>
              ))}
            </div>
          )}

          {isPreviewBusy ? (
            <div className="reports-empty-state reports-loading-state">
              Loading report...
            </div>
          ) : (
          <>
          <div className="reports-control-row">
            <div className="reports-search-box">
              <input
                type="text"
                placeholder="Search rows..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className="reports-search-input"
                aria-label="Search rows in preview table"
              />
            </div>
          </div>
          <div className="reports-table-wrap">
            <table>
              <thead>
                <tr>
                  {previewColumns.map((column) => (
                    <th key={column} className={getColumnAlignment(column)}>{formatColumnLabel(column)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visiblePreviewRows.length === 0 ? (
                  <tr>
                    <td colSpan={Math.max(previewColumns.length, 1)} className={previewReport.error ? 'reports-empty-cell reports-error-cell' : 'reports-empty-cell'}>
                      {previewReport.error || 'No report data found.'}
                    </td>
                  </tr>
                ) : visiblePreviewRows.map((row, rowIndex) => (
                  <tr key={`${previewReport.type}-${row.id || rowIndex}`}>
                    {previewColumns.map((column) => (
                      <td key={column} className={getColumnAlignment(column)}>{formatCell(column, row[column])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
          )}
        </section>
      )}
      </div>
    </div>
  );
}
